import React, { ComponentType, useContext } from 'react';
import { Route, ScrollToTop, Routes } from '@thd-olt-component-react/router';
import { AppContext } from '@thd-nucleus/app-render';
import { TAccessPolicy } from '@ocm_customer/account_pro-ui-access';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { LeftNavDataProvider } from '@ocm_customer/account_navigation';
import { ThemeProvider } from '@material-ui/core';
import { useReturnStore } from './stores/returnStore';
import CommonProvider from './contexts/CommonContext';
import { Error } from './pages/Error';
import { getRoutes } from './routes.js';
import { THDTheme } from './theme/theme';
import Spinner from './components/shared/Spinner';
import { TTrialsObject, TStateManagementResponse } from './models/common';
import { redirectToSignIn } from './utils/http-helper';
import './styles/app.scss';
import useReturnSession from './hooks/useReturnSession';

type ServerPages = Record<string, ComponentType<Record<string, unknown>>>;

interface AppProps {
  customerType: string;
  customerRole: string;
  policyData?: TAccessPolicy[] | null;
  trialsData?: TTrialsObject | null;
  userData?: TStateManagementResponse | null;
  enrollmentsData?: { isEnrolledInPerksProgram: boolean } | null;
  serverPages?: ServerPages;
}

export const App = ({
  customerType,
  customerRole,
  policyData = null,
  trialsData = null,
  enrollmentsData = null,
  userData = null,
  serverPages = {},
}: AppProps) => {
  // will call to get the return session data globally and only once
  useReturnSession();
  const { instance } = useContext(AppContext);
  const { currentRouteName, currentRouteComponent } = instance.router;
  const routes = getRoutes(
    currentRouteName,
    currentRouteComponent,
    serverPages,
  );
  const { isLoggedIn } = useThdCustomer();
  const orderDetails = useReturnStore((state) => state.orderDetailsV2);
  const isMilitaryExchangeGuest = useReturnStore(
    (state) => state.isMilitaryExchangeGuest,
  );

  React.useEffect(() => {
    // Check for orderdetails data to see if data has been loaded & isMilitaryExchangeGuest has been retrieved.
    // If guest is false, user has to be logged in. Otherwise, no need to check session.
    if (orderDetails) {
      if (!isMilitaryExchangeGuest) {
        if (!isLoggedIn && currentRouteName !== 'Home') {
          redirectToSignIn();
        }
      }
    }
  }, [currentRouteName, isMilitaryExchangeGuest, orderDetails]);

  return (
    <>
      <LeftNavDataProvider
        defaultPolicyData={policyData}
        defaultTrialsData={trialsData}
        defaultUserData={userData}
        defaultEnrollmentData={enrollmentsData}
      >
        <ThemeProvider theme={THDTheme}>
          <CommonProvider>
            <>
              <Routes>
                <Route
                  exact
                  path={routes.returns.path}
                  element={
                    <routes.returns.component
                      customerType={customerType}
                      customerRole={customerRole}
                    />
                  }
                />
                <Route
                  exact
                  path={routes.returnSummary.path}
                  element={
                    <routes.returnSummary.component
                      customerType={customerType}
                      customerRole={customerRole}
                    />
                  }
                />
                <Route
                  exact
                  path={routes.home.path}
                  element={<routes.home.component />}
                />
                <Route path="/error" element={<Error />} />
                <Route element={<Error />} />
              </Routes>
              <Spinner />
            </>
          </CommonProvider>
        </ThemeProvider>
      </LeftNavDataProvider>
      <ScrollToTop />
    </>
  );
};

App.propTypes = {};

App.defaultProps = {};
